import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    Box,
    Button,
    Typography,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';

import BackdropLoader from '../../../../components/common/BackdropLoader';
import Swal from 'sweetalert2';
import PlantForm, { type PlantFormData } from '../PlantForm';
import { plantValidationSchema, zodResolver } from '../../plantParser';
import { useUpdatePlantMutation, useGetPlantByIdQuery } from '../../../../api/services/plantApi';

const PlantEdit: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { data: plantData, isLoading: isLoadingPlant } = useGetPlantByIdQuery(id);
    const [updatePlant, { isLoading: isUpdating }] = useUpdatePlantMutation();

    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    const {
        control,
        // watch,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
        setError,
        clearErrors
    } = useForm<PlantFormData>({
        mode: 'onBlur',
        resolver: zodResolver(plantValidationSchema),
        defaultValues: {
            name: '',
            description: '',
            autoClosureTime: '',
            shifts: [{ start: '', end: '' }],
            lines: '',
            timezone:''
        },
    });
    // console.log(watch().autoClosureTime, 'controlcontrolcontrol');



    useEffect(() => {
        if (plantData?.plant) {
            const plant = plantData.plant;

            // Parse shifts
            const shifts = plant.shiftDetails?.map((shift: any) => ({
                start:shift.STARTTIME,
                end: shift.ENDTIME,
            })) || [{ start: '', end: '' }];

            // Calculate line count
            const lineCount = plant.lineDetails?.length || 0;
            const lineCountStr = lineCount > 0 ? lineCount.toString().padStart(2, '0') : '';

            const formData: PlantFormData = {
                name: plant.PLANTNAME || '',
                description: plant.PLANTDESC || '',
                displayName:plant.PLANTDISPLAYNAME||'',
                autoClosureTime: plant.MICRO_STOPPAGE_TIME || '',
                shifts: shifts,
                lines: lineCountStr,
                timezone:plant.TIMEZONE||''
            };



            // Reset form with the fetched data
            reset(formData);
        }
    }, [plantData, reset]);


    // Check if form is valid and has changes
    const isFormValidAndDirty = () => {
        if (!isDirty) return false;

        const hasErrors = Object.keys(errors).length > 0 || Object.keys(formErrors).length > 0;
        return !hasErrors;
    };

    const onFormSubmit = async (data: PlantFormData) => {
        // Clear previous errors
        setFormErrors({});
        clearErrors();

        // Validate using safeParse
        const validationResult = plantValidationSchema.safeParse(data);

        if (!validationResult.success) {
            // Handle validation errors
            const fieldErrors: Record<string, string> = {};

            validationResult.error.issues.forEach((issue: any) => {
                const path = issue.path.join('.');
                fieldErrors[path] = issue.message;

                // Set errors for react-hook-form
                if (issue.path.length === 1) {
                    setError(issue.path[0] as keyof PlantFormData, {
                        type: 'manual',
                        message: issue.message
                    });
                } else if (issue.path.length === 2) {
                    // For array fields like shifts[0].start
                    const fieldName = `${issue.path[0]}.${issue.path[1]}` as any;
                    setError(fieldName, {
                        type: 'manual',
                        message: issue.message
                    });
                } else if (issue.path.length === 3) {
                    // For nested array fields like shifts[0].start
                    const fieldName = `${issue.path[0]}.${issue.path[1]}.${issue.path[2]}` as any;
                    setError(fieldName, {
                        type: 'manual',
                        message: issue.message
                    });
                }
            });

            setFormErrors(fieldErrors);

            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please fix the validation errors before submitting.',
                confirmButtonColor: '#003EB0',
                confirmButtonText: 'OK'
            });
            return;
        }

        try {
            const apiPayload = {
                PLANTNAME: data.name,
                PLANTDESC: data.description,
                MICRO_STOPPAGE_TIME: `00:${data.autoClosureTime}`,
                OUID: plantData?.plant?.OUID || "temp",
                PLANTDISPLAYNAME:data.displayName,
                TIMEZONE:data.timezone,
                lineCount: parseInt(data.lines),
                shifts: data.shifts.map((shift, index) => ({
                    SHIFTNAME: String(index + 1),
                    SHIFTDESC: `Shift ${index + 1}`,
                    STARTTIME: shift.start,
                    ENDTIME: shift.end,
                    PLANTNAME: data.name
                }))
            };

            await updatePlant({ ID: id, formData: apiPayload }).unwrap();

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Plant updated successfully',
                confirmButtonColor: '#003EB0',
                confirmButtonText: 'OK'
            }).then(() => {
                navigate('/plant');
            });

        } catch (err: any) {
            console.log(err, 'errerrerr');

            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: err?.data?.message || 'Failed to update plant. Please try again.',
                confirmButtonColor: '#003EB0',
                confirmButtonText: 'OK'
            });
        }
    };

    const handleCancel = () => {
        navigate('/plant');
    };

    const isLoading = isLoadingPlant || isUpdating;

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container sx={{ width: '100%' }}>
                <BackdropLoader openStates={isLoading} />
                <Grid size={{ xs: 6 }} sx={{ width: "50%" }}>
                    <Grid container sx={{ width: '100%' }}>
                        <Grid size={{ xs: 'auto' }}>
                            <ArrowBackIcon sx={{ mr: 1, color: '#1976d2' }} onClick={handleCancel} />
                        </Grid>
                        <Grid size={{ xs: 'auto' }}>
                            <Typography variant="h6" sx={{ color: '#172B4D', fontWeight: 500 }}>
                                Edit Plant
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ width: '50%', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        onClick={handleCancel}
                        variant="outlined"
                        disabled={isLoading}
                        sx={{
                            textTransform: 'none',
                            color: '#172B4D',
                            border: '1px solid rgba(9, 30, 66, 0.31)',
                            borderColor: '#ddd',
                            background: "rgba(9, 30, 66, 0.06)",
                            borderRadius: '6px',
                            mr: 1,
                            '&:hover': {
                                borderColor: '#bbb',
                                bgcolor: '#f5f5f5'
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!isFormValidAndDirty() || isLoading}
                        onClick={handleSubmit(onFormSubmit)}
                        sx={{
                            borderRadius: '6px',
                            textTransform: 'none',
                            background: '#003EB0',
                            '&:hover': {
                                bgcolor: '#1565c0'
                            },
                        }}
                    >
                        {isUpdating ? 'Updating...' : 'Update Plant'}
                    </Button>
                </Grid>
            </Grid>

            <Box sx={{ p: 4, background: '#FFF', mt: 1 }}>
                <form>
                    <PlantForm
                        control={control}
                        errors={errors}
                        isLoading={isLoading}
                        formErrors={formErrors}
                    />
                </form>
            </Box>
        </Box>
    );
};

export default PlantEdit;